import { AdminApiError, type AdminClient } from "../api.js";
import { clear, h } from "../dom.js";
import type {
  Company,
  CompanySlug,
  LinkInvitationIssueResponse,
  LinkSessionResponse,
  StaffRole,
} from "../types.js";
import { card, disclosure, distinctControlNames, emptyState, pageHeader } from "../ui/components.js";
import { advice } from "../ui/capabilities.js";
import * as face from "../ui/face.js";
import { readerRole } from "../ui/facts.js";
import { failureCopy, outcomeSentence } from "../ui/failure.js";
import { navLabel, sentence } from "../ui/sentences.js";
import { forgetPossiblySent, runWrite } from "../ui/write.js";
import { INVALID_EXPIRY_COPY } from "./tokens.js";

const PAGE_TITLE = navLabel("invitations");

/** One home for the revoke confirmation both onboarding pages show. */
export const INVITATION_REVOKED_COPY = "Invitation revoked.";

/**
 * How long the Hosted Link URL the owner copies actually works (the Worker's
 * EXCHANGE_WINDOW_MS, 30 minutes). The invitation row's own expiry is a
 * different, longer clock; the link is what the person is handed.
 */
const LINK_LIFETIME_COPY = (role: StaffRole): string => sentence("bank_link_works_for", role);

export interface InvitationsPageOptions {
  client: AdminClient;
  root: HTMLElement;
  /** Who is reading; the shell passes it where it knows (optional). */
  role?: StaffRole;
  /** Return false if a newer navigate superseded this render; drop results. */
  isCurrent?: () => boolean;
}

/** Issue a Hosted Link URL. Shown once; send it to the person linking a bank. */
export async function renderInvitationsPage(opts: InvitationsPageOptions): Promise<void> {
  const { client, root } = opts;
  const role = readerRole(opts.role);
  clear(root);
  const page = h("div", { class: "settings-stack" });
  root.append(page);

  if (role === "account_manager") {
    page.append(
      pageHeader(PAGE_TITLE, "Only an owner can create or revoke a bank-connection link."),
      emptyState(advice("issue_bank_link", role)),
    );
    return;
  }

  page.append(h("p", { class: "muted" }, ["Loading companies…"]));

  let companies: Company[];
  try {
    const loaded = await client.listCompanies();
    companies = loaded.companies;
  } catch (error) {
    if (!(opts.isCurrent?.() ?? true)) return;
    if (
      error instanceof AdminApiError &&
      (error.status === 401 || error.status === 403 || error.status === 503)
    ) {
      throw error;
    }
    clear(page);
    const retry = h(
      "button",
      { type: "button", class: "btn btn-primary", "data-action": "retry", "aria-label": "Retry" },
      ["Try again"],
    );
    retry.addEventListener("click", () => {
      void renderInvitationsPage(opts);
    });
    page.append(
      pageHeader(PAGE_TITLE, "Create a one-time link so someone else can connect a bank."),
      h("div", { class: "error-state", role: "alert" }, [
        h("p", {}, ["We could not load companies. Try again."]),
        disclosure(
          "Show details",
          h("p", {}, [failureCopy(error, { role, context: "read" }).detail ?? "Request failed"]),
        ),
        retry,
      ]),
    );
    return;
  }
  if (!(opts.isCurrent?.() ?? true)) return;
  clear(page);
  page.append(
    pageHeader(
      PAGE_TITLE,
      `Create a one-time link for the person connecting a bank. Copy it when you create it: it ${LINK_LIFETIME_COPY(role)}. To connect a bank yourself, open ${navLabel("balances")} and choose Connect account.`,
    ),
  );

  const onceBanner = h("div", {
    class: "once-banner",
    "data-role": "invite-once",
    hidden: "true",
  }, []);
  /**
   * A failed second Issue must not replace the banner still showing an earlier
   * link. While a link is on screen the failure goes here, beside it, and the
   * banner keeps the link (rd round 11 review, MEDIUM 1).
   */
  const issueError = h("div", {
    class: "banner banner-err",
    role: "alert",
    "data-role": "invite-issue-error",
    hidden: "true",
  }, []);
  page.append(onceBanner, issueError);
  /** The invitation whose link the banner is showing, or null (R8-08). */
  let shownInviteId: string | null = null;
  const hideIssueError = (): void => {
    clear(issueError);
    issueError.hidden = true;
    issueError.setAttribute("hidden", "true");
  };
  const hideOnce = (): void => {
    clear(onceBanner);
    onceBanner.hidden = true;
    onceBanner.setAttribute("hidden", "true");
    onceBanner.className = "once-banner";
    shownInviteId = null;
    // The failure note belonged to an Issue that sat beside the link; it goes
    // with it.
    hideIssueError();
  };
  const onRevoked = (id: string): void => {
    if (shownInviteId === id) hideOnce();
  };
  /**
   * Where an Issue failure or a refused expiry goes. While a link is visible
   * the message sits beside it; with no link on screen the once banner is free
   * to carry the message, and its id tracking is cleared with it.
   */
  const showIssueError = (message: string, detail?: string): void => {
    if (shownInviteId !== null) {
      fillBanner(issueError, message, detail, "banner banner-err");
      return;
    }
    shownInviteId = null;
    fillBanner(onceBanner, message, detail);
  };

  const revokeBanner = h("p", {
    class: "banner",
    "data-role": "invite-revoke",
    hidden: "true",
  }, []);
  page.append(revokeBanner);

  const companySelect = h("select", { "aria-label": "Company" });
  const directory = face.createCompanyDirectory(
    companies.map((company) => ({ slug: company.slug, displayName: company.displayName })),
    { complete: true },
  );
  for (const company of companies.filter((c: Company) => c.isActive)) {
    companySelect.append(
      h("option", { value: company.slug }, [face.company(company.slug, directory, "sentence")]),
    );
  }

  const labelInput = h("input", {
    type: "text",
    placeholder: "Short name (optional)",
    "aria-label": "Label",
  });

  const expiresInput = h("input", {
    type: "datetime-local",
    "aria-label": "Expires at",
  });
  // DECIDED (owner, round-11 decision 3; rd-r12-background.md §2e item 3):
  // "set the default to 30 minutes so the two match." The invitation's own
  // expiry now matches the link window the page states.
  const defaultExpiry = new Date(Date.now() + 30 * 60 * 1000);
  let seededExpiry = toLocalInput(defaultExpiry);
  expiresInput.value = seededExpiry;
  /** True once the person edits the field; an untouched default is re-seeded. */
  let expiryTouched = false;
  expiresInput.addEventListener("input", () => {
    expiryTouched = true;
  });

  const issueBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "aria-label": "Issue link invitation",
    },
    ["Issue invitation"],
  );

  const issuedList = h("ul", { class: "invite-list", "data-role": "invite-list" });
  issuedList.append(
    h("li", { class: "empty", "data-role": "invite-empty" }, [
      "No links yet this session. Create one above, copy it, and send it to the person connecting the bank.",
    ]),
  );

  /**
   * Give the person the way out of a possibly-sent block after they have
   * checked for the invitation themselves. This page has no list read of
   * issued invitations (the route does not exist), so the honest path is an
   * explicit "I checked" that clears the memory; a second automatic press is
   * never offered (item B3b-3).
   */
  const offerForget = (key: string, host: HTMLElement): void => {
    const checked = h(
      "button",
      { type: "button", class: "btn btn-secondary", "data-action": "checked-link-issue" },
      ["I checked — try again"],
    );
    checked.addEventListener("click", () => {
      forgetPossiblySent(key);
      hideIssueError();
    });
    host.append(checked);
  };

  /** After a failed revoke: say the invitation may still be live, and offer it again. */
  const offerRevokeAgain = (id: string, host: HTMLElement): void => {
    const again = h(
      "button",
      { type: "button", class: "btn btn-secondary", "data-action": "revoke-invite-again" },
      ["Revoke the invitation"],
    );
    again.addEventListener("click", () => {
      void (async () => {
        again.disabled = true;
        const outcome = await runWrite({
          key: `link-invite-revoke:${id}`,
          scope: host,
          gate: () => null,
          send: () => client.revokeLinkInvitation(id),
          stillCurrent: () => true,
        });
        if (outcome.kind === "landed") {
          clear(host);
          host.hidden = false;
          host.removeAttribute("hidden");
          host.className = "once-banner banner-ok";
          host.append(document.createTextNode(INVITATION_REVOKED_COPY));
          return;
        }
        const copy = failureCopy(outcome.error, { role, context: "write" });
        again.disabled = false;
        host.append(document.createTextNode(` ${copy.sentence}`));
      })();
    });
    host.append(again);
  };

  issueBtn.addEventListener("click", () => {
    void (async () => {
      const company = companySelect.value;
      if (!company) return;
      // A page left open past its untouched default must not send a stale
      // value the Worker refuses, then resend it on every press (round 2 M9).
      // A field the person cleared or edited is theirs: only the exact seeded
      // value is re-seeded.
      if (!expiryTouched && expiresInput.value === seededExpiry) {
        seededExpiry = toLocalInput(new Date(Date.now() + 30 * 60 * 1000));
        expiresInput.value = seededExpiry;
      }

      // A blank or unparseable datetime-local is an Invalid Date, and
      // `toISOString()` throws on one. Refuse it before it reaches the client
      // (rd round 11 review, SHOULD-FIX 9).
      const expiresAt = parseExpiresAt(expiresInput.value);
      if (expiresAt === null) {
        // Beside the link if one is showing: the person may not have copied it
        // yet (rd round 11 review, MEDIUM 1).
        showIssueError(INVALID_EXPIRY_COPY);
        return;
      }
      const key = `link-invite:${company}`;
      const held: {
        landed: { issued: LinkInvitationIssueResponse; session: LinkSessionResponse } | undefined;
        unrevokedId: string | undefined;
      } = { landed: undefined, unrevokedId: undefined };
      const outcome = await runWrite({
        key,
        // Only the issue form goes busy: the issued rows (and their revoke
        // controls) stay usable while a request is out.
        scope: issueBtn.closest(".card") ?? page,
        gate: () => null,
        send: async () => {
          const issued = await client.issueLinkInvitation(company, {
            expiresAt,
            ...(labelInput.value.trim() ? { label: labelInput.value.trim() } : {}),
          });
          try {
            const session = await client.openLinkSession(issued.token);
            return { issued, session };
          } catch (error) {
            // The invitation exists but the link could not be opened. Revoke it
            // so the retry cannot leave a live invitation behind. If that
            // revoke fails, say so rather than swallowing it (rc-2 sister 5).
            try {
              await client.revokeLinkInvitation(issued.id);
            } catch {
              held.unrevokedId = issued.id;
            }
            throw error;
          }
        },
        afterLanded: (value) => {
          held.landed = value;
        },
        stillCurrent: () => true,
        blockWhenPossiblySent: true,
      });
      if (outcome.kind === "landed") {
        if (held.landed === undefined) return;
        const done: { issued: LinkInvitationIssueResponse; session: LinkSessionResponse } = held.landed;
        hideIssueError();
        showOnce(onceBanner, done.session.hostedLinkUrl, done.issued.id);
        shownInviteId = done.issued.id;
        const empty = issuedList.querySelector('[data-role="invite-empty"]');
        if (empty !== null) empty.remove();
        // The hosted link, not the invitation, is what the person is handed:
        // its clock is 30 minutes from issue (R8-19, review MEDIUM 5).
        const linkWorksUntil = new Date(Date.now() + 30 * 60 * 1000);
        issuedList.prepend(
          renderIssuedRow(
            done.issued.id,
            done.issued.company,
            linkWorksUntil,
            client,
            revokeBanner,
            directory,
            role,
            onRevoked,
          ),
        );
        // Two links for one company with the same end-time would read
        // identically: tell them apart by position, never by id (round 3).
        distinctControlNames(issuedList, '[data-action="revoke-invite"]');
        return;
      }
      const copy =
        outcome.kind === "unknown"
          ? {
              sentence: outcomeSentence({ kind: "unknown", blocked: true }, role),
              detail:
                outcome.error === undefined
                  ? null
                  : failureCopy(outcome.error, { role, context: "write" }).detail,
            }
          : outcome.error === undefined
            ? { sentence: outcomeSentence(outcome, role), detail: null }
            : failureCopy(outcome.error, { role, context: "write" });
      showIssueError(copy.sentence, copy.detail ?? undefined);
      const host = shownInviteId !== null ? issueError : onceBanner;
      if (held.unrevokedId !== undefined) {
        host.append(
          document.createTextNode(
            " The invitation was created and could not be revoked, so it may still be live.",
          ),
        );
        offerRevokeAgain(held.unrevokedId, host);
        return;
      }
      offerForget(key, host);
    })();
  });

  page.append(
    card(
      h("div", { class: "form-row" }, [
        h("label", {}, ["Company", companySelect]),
        h("label", {}, ["Label", labelInput]),
        h("label", {}, ["Expires", expiresInput]),
        issueBtn,
      ]),
    ),
  );
  page.append(h("h2", {}, ["Issued this session"]));
  page.append(issuedList);
}

function renderIssuedRow(
  id: string,
  company: CompanySlug,
  linkWorksUntil: Date,
  client: AdminClient,
  revokeBanner: HTMLElement,
  directory: face.CompanyDirectory,
  role: StaffRole,
  onRevoked?: (id: string) => void,
): HTMLLIElement {
  const row = h("li", { class: "invite-row", "data-invite-id": id }, [
    h("span", {}, [
      `${face.company(company, directory, "sentence")} · Link works until ${localDateTimeText(linkWorksUntil)}`,
    ]),
    disclosure("Show details", h("p", {}, [id])),
  ]);
  const revokeBtn = h("button", {
    type: "button",
    class: "btn btn-ghost",
    "data-action": "revoke-invite",
    // A person reads the appearance, never the raw invitation id. The visible
    // word stays "Revoke"; the accessible name names the company and when the
    // link stops working, so two invites are told apart (round 2 M8).
    "aria-label": face.ariaFor(
      `Revoke the invitation for ${face.company(company, directory, "sentence")}, link works until ${localDateTimeText(linkWorksUntil)}`,
    ),
  }, ["Revoke"]);
  revokeBtn.addEventListener("click", () => {
    void (async () => {
      revokeBtn.disabled = true;
      const outcome = await runWrite({
        key: `link-invite-revoke:${id}`,
        scope: row,
        gate: () => null,
        send: () => client.revokeLinkInvitation(id),
        stillCurrent: () => true,
      });
      if (outcome.kind === "landed") {
        clear(revokeBanner);
        revokeBanner.hidden = false;
        revokeBanner.removeAttribute("hidden");
        revokeBanner.className = "banner banner-ok";
        revokeBanner.append(
          document.createTextNode(INVITATION_REVOKED_COPY),
          disclosure("Show details", h("p", {}, [id])),
        );
        row.classList.add("revoked");
        // A dead invitation's link must not stay on screen (R8-08).
        onRevoked?.(id);
        return;
      }
      clear(revokeBanner);
      revokeBanner.hidden = false;
      revokeBanner.removeAttribute("hidden");
      revokeBanner.className = "banner banner-err";
      const copy = failureCopy(outcome.error, { role, context: "write" });
      revokeBanner.append(document.createTextNode(copy.sentence));
      if (copy.detail !== null) {
        revokeBanner.append(disclosure("Show details", h("p", {}, [copy.detail])));
      }
      // The button is usable again: the retry IS the offer to revoke again.
      revokeBtn.disabled = false;
    })();
  });
  row.append(revokeBtn);
  return row;
}

function showOnce(el: HTMLElement, hostedLinkUrl: string, id: string): void {
  clear(el);
  el.hidden = false;
  el.removeAttribute("hidden");
  el.className = "once-banner banner-ok";
  el.append(
    h("p", {}, [`Copy this link now. It ${sentence("bank_link_works_for", "owner")} and will not be shown again.`]),
    h("code", { "data-role": "hosted-link-url", "data-invite-id": id }, [hostedLinkUrl]),
  );
}

/**
 * The deadline as the reader's own clock reads it. `timeAgoText` prints UTC
 * (`clockText` uses `getUTC*`), which is four to eight hours off in US zones —
 * wrong for a "you can still use this until" answer. No `timeZone` option, so
 * the browser's zone decides (rd round 11 review, MEDIUM 2).
 */
function localDateTimeText(when: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(when);
}

/** Write a message into a banner, raw detail only behind Show details. */
function fillBanner(
  el: HTMLElement,
  message: string,
  detail?: string,
  className = "once-banner banner-err",
): void {
  clear(el);
  el.hidden = false;
  el.removeAttribute("hidden");
  el.className = className;
  el.append(document.createTextNode(message));
  if (detail !== undefined && detail !== "" && detail !== message) {
    el.append(disclosure("Show details", h("p", {}, [detail])));
  }
}

/** Guard empty/invalid expiry before `toISOString()`, which throws on Invalid Date. */
function parseExpiresAt(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  // A date at or before now is not a lifetime: the Worker refuses it, and the
  // person would resend the same value on every press (round 2 M9).
  if (parsed.getTime() <= Date.now()) return null;
  return parsed.toISOString();
}

function toLocalInput(date: Date): string {
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
